import { message } from "antd";
import { useEffect, useMemo, useState } from "react";
import Form from "V2.0/components/Form";
import Joi from "joi-browser";
import { useThirdPartySettings } from "./useThirdPartySettings";
import * as thirdpartyService from "V2.0/services/thirdpartyService";

const acuityCalendarSettingsModel = {
  userId: {
    template: {
      name: "userId",
      label: "User Id",
      type: "text",
    },
    schema: Joi.string().required().label("User Id"),
  },
  apiKey: {
    template: {
      name: "apiKey",
      label: "API key",
      type: "text",
    },
    schema: Joi.string().required().label("API Key"),
  },
  calendarId: {
    template: {
      name: "calendarId",
      label: "Calendar Id",
      type: "text",
    },
    schema: Joi.string().allow(null, "").label("Calendar Id"),
  },
  appointmentTypeId: {
    template: {
      name: "appointmentTypeId",
      label: "Appointment type Id",
      type: "text",
    },
    schema: Joi.string().required().label("Appointment Type Id"),
  },
  SECTION: {
    template: {
      name: "SECTION",
      label: "Backup Details",
    },
  },
  firstName: {
    template: {
      name: "firstName",
      label: "First Name",
      type: "text",
    },
    schema: Joi.string().allow(null, ""),
  },
  lastName: {
    template: {
      name: "lastName",
      label: "Last Name",
      type: "text",
    },
    schema: Joi.string().allow(null, ""),
  },
  email: {
    template: {
      name: "email",
      label: "Email",
      type: "email",
    },
    schema: Joi.string().allow(null, ""),
  },
};

const acuityTemplate = Object.keys(acuityCalendarSettingsModel).map(
  (field) => acuityCalendarSettingsModel[field].template,
);
const acuitySchema = Object.keys(acuityCalendarSettingsModel).reduce(
  (acc, field) => {
    if (field === "SECTION") {
      return acc;
    }

    return {
      ...acc,
      [field]: acuityCalendarSettingsModel[field].schema,
    };
  },
  {},
);

function AcuityCalendarForm(props) {
  const { projectId, isViewOnly } = props;
  const [loading, setLoading] = useState(false);
  const { thirdPartySettings, fetchThirdPartySettings } =
    useThirdPartySettings(projectId);

  const [calendarSettings, setCalendarSettings] = useState({
    calendarId: "",
    userId: "",
    apiKey: "",
    appointmentTypeId: "",
    firstName: "",
    lastName: "",
    email: "",
  });

  useEffect(() => {
    if (thirdPartySettings?.calendarSettings?.acuitySettings) {
      const { calendarId, userId, apiKey, appointmentTypeId, backupDetails } =
        thirdPartySettings.calendarSettings.acuitySettings;
      const { firstName, lastName, email } = backupDetails;
      setCalendarSettings({
        calendarId,
        userId,
        apiKey,
        appointmentTypeId,
        firstName,
        lastName,
        email,
      });
    }
  }, [thirdPartySettings]);

  const updatedAcuityTemplate = useMemo(() => {
    if (!isViewOnly) {
      return acuityTemplate;
    }

    return acuityTemplate.map((field) => {
      return {
        ...field,
        disabled: isViewOnly,
      };
    });
  }, [isViewOnly]);

  const updatedButtonInfo = !isViewOnly
    ? {
        label: "Save Calendar settings",
        style: "entire-width",
      }
    : {};

  const onSave = async (data) => {
    const { firstName, lastName, email, ...rest } = data;
    const payload = {
      ...rest,
      backupDetails: {
        firstName,
        lastName,
        email,
      },
    };
    try {
      message.loading("Updating Acuity calendar settings");
      setLoading(true);
      const response = await thirdpartyService.thirdpartyUpdate(
        {
          calendarSettings: {
            calendarProvider: "Acuity",
            acuitySettings: payload,
          },
        },
        projectId,
      );
      if (response.status === 200) {
        setLoading(false);
        message.destroy();
        fetchThirdPartySettings();
        message.success("Settings updated");
      }
    } catch (e) {
      setLoading(false);
      message.destroy();
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error,
      );
    }
  };

  return (
    <div className="border rounded-lg flex flex-col mx-auto gap-y-2 px-8 py-4">
      <Form
        formTitle="Acuity Scheduling"
        template={updatedAcuityTemplate}
        inputField={calendarSettings}
        schema={acuitySchema}
        loading={loading}
        setLoading={setLoading}
        onSubmit={onSave}
        buttonInfo={updatedButtonInfo}
      />
    </div>
  );
}

export { AcuityCalendarForm };
